import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { motion } from 'framer-motion';
import HomeIntro from './HomeIntro';
import PictureTextBlock from './PictureTextBlock';
import DogSlider from './DogSlider';
import MyOtherAdventures from './MyOtherAdventures';
import AlwaysOpenAdventure from './AlwaysOpenAdventure';
import ContactSquare from './SupportSponsorSquare';

const AboutPage = () => {
    const [menuOpen, setMenuOpen] = useState(false);
    const [hovered, setHovered] = useState(false);
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 1024);

    const homeRef = useRef(null);
    const contactRef = useRef(null);
    const adventuresRef = useRef(null);
    const supportRef = useRef(null);
    const dogsRef = useRef(null);

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth <= 1024);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const scrollToSection = (ref) => {
        if (ref.current) {
            ref.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
            setMenuOpen(false);
        }
    };

    const floatingButtonStyle = {
        ...styles.floatingMenuButton,
        width: isMobile ? '36px' : '42px',
        height: isMobile ? '36px' : '42px',
        top: isMobile ? '28px' : '32px',
        left: isMobile ? '18px' : '25px',
    };

    const sideMenuStyle = {
        ...styles.sideMenu,
        top: isMobile ? '100px' : '130px', // lowered on mobile
    };

    const headerStyle = {
        ...styles.header,
        minHeight: isMobile ? '90px' : '130px', // slimmer header on mobile
        padding: isMobile ? '0 20px' : '0 40px',
    };

    const titleStyle = {
        ...styles.title,
        fontSize: isMobile ? '22px' : 'clamp(20px, 5vw, 36px)',
    };

    return (
        <>
            {/* 🟣 Fixed Header */}
            <header style={headerStyle}>
                <h1 style={titleStyle}>
                    <span style={{ color: '#BAD8B6' }}>LindQuest</span>
                    <span style={{ color: '#72BAA9' }}>2</span>
                    <span style={{ color: '#FFF574' }}>NOME</span>
                </h1>
            </header>

            {/* 🍔 Floating Hamburger Button */}
            {createPortal(
                <div
                    style={{
                        ...floatingButtonStyle,
                        transform: hovered ? 'scale(1.1)' : 'scale(1)',
                        boxShadow: hovered
                            ? '0 6px 12px rgba(0,0,0,0.3)'
                            : '0 3px 8px rgba(0,0,0,0.25)',
                    }}
                    onMouseEnter={() => setHovered(true)}
                    onMouseLeave={() => setHovered(false)}
                    onClick={() => setMenuOpen(!menuOpen)}
                >
                    <div style={styles.bar}></div>
                    <div style={styles.bar}></div>
                    <div style={styles.bar}></div>
                </div>,
                document.body
            )}

            {/* 📋 Side Menu + Overlay */}
            {menuOpen &&
                createPortal(
                    <>
                        {/* ✅ Overlay that closes the menu when clicked */}
                        <div
                            style={styles.overlay}
                            onClick={() => setMenuOpen(false)}
                        ></div>

                        <div
                            style={{
                                ...sideMenuStyle,
                                left: menuOpen ? '-20px' : '-280px',
                            }}
                        >
                            <button
                                style={styles.menuButton}
                                onClick={() => scrollToSection(homeRef)}
                            >
                                About Me
                            </button>
                            <button
                                style={styles.menuButton}
                                onClick={() => scrollToSection(supportRef)}
                            >
                                Support
                            </button>
                            <button
                                style={styles.menuButton}
                                onClick={() => scrollToSection(dogsRef)}
                            >
                                Dogs
                            </button>
                            <button
                                style={styles.menuButton}
                                onClick={() => scrollToSection(adventuresRef)}
                            >
                                My Adventures
                            </button>
                            <button
                                style={styles.menuButton}
                                onClick={() => scrollToSection(contactRef)}
                            >
                                Contact
                            </button>
                        </div>
                    </>,
                    document.body
                )}

            {/* 🟢 Main Content */}
            <div
                style={{
                    overflowX: 'hidden',
                    minHeight: '100vh',
                    display: 'flex',
                    flexDirection: 'column',
                }}
            >
                <div style={{ height: isMobile ? '90px' : '130px' }}></div>

                <motion.div
                    style={styles.container}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.5 }}
                >
                    <section ref={homeRef} style={styles.section}>
                        <HomeIntro />
                    </section>

                    <section style={styles.section}>
                        <PictureTextBlock />
                    </section>

                    <section ref={supportRef} style={styles.section}>
                        <AlwaysOpenAdventure />
                    </section>

                    <section ref={dogsRef} style={styles.section}>
                        <DogSlider />
                    </section>

                    <section ref={adventuresRef} style={styles.section}>
                        <MyOtherAdventures />
                    </section>

                    <section ref={contactRef}>
                        <ContactSquare />
                    </section>
                </motion.div>

                <footer style={styles.footer}>© LindQuest2Nome 2026</footer>
            </div>
        </>
    );
};

// 🎨 Styles
const styles = {
    header: {
        width: '100%',
        backgroundColor: '#52357B',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        boxSizing: 'border-box',
        position: 'fixed',
        top: 0,
        left: 0,
        zIndex: 200,
    },
    title: {
        margin: 0,
        fontFamily: "'Libre Baskerville', serif",
        fontWeight: 300,
        textAlign: 'center',
        color: '#FFF',
    },
    floatingMenuButton: {
        position: 'fixed',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        borderRadius: '50%',
        backgroundColor: '#72BAA9',
        boxShadow: '0 3px 8px rgba(0,0,0,0.25)',
        cursor: 'pointer',
        zIndex: 400,
        transition: 'all 0.2s ease',
    },
    bar: {
        width: '60%',
        height: '3px',
        backgroundColor: '#52357B',
        borderRadius: '2px',
        margin: '3px 0',
    },
    overlay: {
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        backgroundColor: 'rgba(0,0,0,0.3)',
        zIndex: 300,
    },
    sideMenu: {
        position: 'fixed',
        width: '260px',
        backgroundColor: '#52357B',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '25px',
        padding: '30px 0',
        transition: 'left 0.3s ease',
        zIndex: 350,
        borderRadius: '0 20px 20px 0',
        boxShadow: '4px 0 10px rgba(0,0,0,0.3)',
    },
    menuButton: {
        fontSize: '20px',
        color: '#FFF2AF',
        background: 'none',
        border: 'none',
        cursor: 'pointer',
        fontFamily: "'Libre Baskerville', serif",
    },
    container: {
        backgroundColor: '#5459AC',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'flex-start',
        padding: '20px',
        boxSizing: 'border-box',
    },
    section: {
        width: '100%',
    },
    footer: {
        backgroundColor: '#3E4389',
        color: '#FFF2AF',
        width: '100%',
        textAlign: 'center',
        padding: '20px 0',
        fontFamily: "'Libre Baskerville', serif",
        fontSize: '16px',
        fontWeight: 500,
    },
};

export default AboutPage;
